import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  DollarSign,
  ShieldCheck,
  UsersRound,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { t } from "@/i18n";

// Définition par clés pour supporter la traduction à la volée dans le rendu
const BENEFITS = [
  {
    titleKey: "affiliateprogram.benefit_1_title",
    descKey: "affiliateprogram.benefit_1_desc",
  },
  {
    titleKey: "affiliateprogram.benefit_2_title",
    descKey: "affiliateprogram.benefit_2_desc",
  },
  {
    titleKey: "affiliateprogram.benefit_3_title",
    descKey: "affiliateprogram.benefit_3_desc",
  },
  {
    titleKey: "affiliateprogram.benefit_4_title",
    descKey: "affiliateprogram.benefit_4_desc",
  },
];

function statusLabel(status) {
  if (status === "pending") return t("status.pending");
  if (status === "approved") return t("status.approved");
  if (status === "rejected") return t("status.rejected");
  return t("status.none");
}

export default function AffiliateProgram() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [application, setApplication] = useState(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const loadApplication = useCallback(async () => {
    if (!user) return;
    try {
      setApplication((await api.get("/affiliate/application")).data);
    } catch {}
  }, [user]);

  useEffect(() => {
    loadApplication();
  }, [loadApplication]);

  const apply = async (event) => {
    event.preventDefault();
    if (!user) {
      nav("/auth", { state: { from: "/affiliate-program" } });
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/affiliate/apply", {
        email: user.email,
        message,
      });
      setApplication(data);
      toast.success(t("toast.success_apply"));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("toast.error_apply"));
    } finally {
      setSending(false);
    }
  };

  const currentStatus =
    application?.status || user?.affiliate_application_status;

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <section className="rounded-3xl bg-primary p-7 text-primary-foreground sm:p-10">
        <div className="max-w-3xl">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold">
            <DollarSign size={14} /> {t("affiliateprogram.badge")}
          </div>
          <h1 className="font-head text-4xl font-extrabold sm:text-5xl">
            {t("affiliateprogram.titre_principal")}
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed opacity-90">
            {t("affiliateprogram.description")}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href="#demander"
              className="rounded-xl bg-ocre px-5 py-3 font-semibold text-white hover:opacity-90"
            >
              {t("affiliateprogram.bouton_faire_demande")}
            </a>
            <Link
              to="/pricing"
              className="rounded-xl border border-white/40 px-5 py-3 font-semibold hover:bg-white/10"
            >
              {t("affiliateprogram.bouton_voir_plans")}
            </Link>
          </div>
        </div>
      </section>

      <section>
        <div className="mb-4">
          <div className="text-xs font-bold uppercase tracking-[0.16em] text-primary">
            {t("affiliateprogram.modalites_surtitre")}
          </div>
          <h2 className="mt-1 font-head text-2xl font-bold">
            {t("affiliateprogram.modalites_titre")}
          </h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {BENEFITS.map(({ titleKey, descKey }) => (
            <div key={titleKey} className="rounded-2xl border bg-card p-5">
              <CheckCircle2 size={19} className="mb-3 text-emerald-600" />
              <h3 className="font-head text-lg font-bold">{t(titleKey)}</h3>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                {t(descKey)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-4 rounded-2xl border bg-card p-5 sm:grid-cols-3 sm:p-6">
        <div>
          <div className="text-xs font-bold uppercase text-primary">
            {t("affiliateprogram.etape_1_titre")}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("affiliateprogram.etape_1_desc")}
          </p>
        </div>
        <div>
          <div className="text-xs font-bold uppercase text-primary">
            {t("affiliateprogram.etape_2_titre")}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("affiliateprogram.etape_2_desc")}
          </p>
        </div>
        <div>
          <div className="text-xs font-bold uppercase text-primary">
            {t("affiliateprogram.etape_3_titre")}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("affiliateprogram.etape_3_desc")}
          </p>
        </div>
      </section>

      <section
        id="demander"
        className="scroll-mt-24 rounded-3xl border-2 border-primary/20 bg-primary/5 p-6 sm:p-8"
      >
        <div className="flex items-start gap-3">
          <div className="rounded-2xl bg-primary/10 p-3 text-primary">
            <UsersRound size={22} />
          </div>
          <div>
            <h2 className="font-head text-2xl font-bold">
              {t("affiliateprogram.form_titre")}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("affiliateprogram.form_desc")}
            </p>
          </div>
        </div>
        {!user ? (
          <div className="mt-5 rounded-2xl border border-dashed bg-card p-5">
            <p className="font-semibold">
              {t("affiliateprogram.connect_titre")}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("affiliateprogram.connect_desc")}
            </p>
            <button
              onClick={() =>
                nav("/auth", { state: { from: "/affiliate-program" } })
              }
              className="mt-4 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground"
            >
              {t("affiliateprogram.connect_btn")}
            </button>
          </div>
        ) : user.is_affiliate ? (
          <div className="mt-5 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-5">
            <p className="font-semibold text-emerald-700 dark:text-emerald-300">
              {t("affiliateprogram.actif_titre")}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("affiliateprogram.actif_desc")}
            </p>
            <button
              onClick={() => nav("/affiliate")}
              className="mt-4 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground"
            >
              {t("affiliateprogram.actif_btn")}
            </button>
          </div>
        ) : (
          <form onSubmit={apply} className="mt-5 space-y-3">
            <label className="block text-sm font-semibold">
              {t("affiliateprogram.label_email")}
              <input
                required
                type="email"
                value={user.email}
                readOnly
                className="mt-1 w-full rounded-xl border bg-background px-4 py-2.5 text-sm"
              />
            </label>
            <label className="block text-sm font-semibold">
              {t("affiliateprogram.label_audience")}
              <textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                rows={4}
                maxLength={2000}
                placeholder={t("affiliateprogram.placeholder_audience")}
                className="mt-1 w-full resize-y rounded-xl border bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 ring-primary"
              />
            </label>
            {currentStatus && (
              <div
                className={`flex items-center gap-2 rounded-xl p-3 text-sm ${currentStatus === "rejected" ? "bg-destructive/10 text-destructive" : "bg-secondary text-foreground"}`}
              >
                <ShieldCheck size={16} /> {statusLabel(currentStatus)}
              </div>
            )}
            <button
              disabled={sending || currentStatus === "pending"}
              className="rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              {sending
                ? t("affiliateprogram.btn_envoi_cours")
                : currentStatus === "pending"
                  ? t("affiliateprogram.btn_deja_envoyee")
                  : currentStatus === "rejected"
                    ? t("affiliateprogram.btn_renvoyer")
                    : t("affiliateprogram.btn_envoi")}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
